<div class="plan-maintain-container">
  <div class="page-header">
    <div>
      <h2>Plan Catalogue</h2>
      <p class="text-muted">Maintain global plans and manage dealer eligibility in one place.</p>
    </div>
    <div class="header-actions">
      <button class="btn btn-outline-secondary me-2" type="button" (click)="syncPlanMappings()" [disabled]="loading" title="Refresh cache after mass changes">
        <span *ngIf="loading" class="spinner-border spinner-border-sm me-1"></span>
        Sync Cache
      </button>
      <button class="btn btn-primary" type="button" (click)="startAddPlan()">Add Plan</button>
    </div>
  </div>

  <div *ngIf="showPlanForm" class="plan-form-card">
    <div class="plan-form-header">
      <div>
        <h3>{{ isEdit ? 'Edit Plan' : 'Create Plan' }}</h3>
        <p class="text-muted mb-0">Adjust plan configuration before publishing to dealers.</p>
      </div>
      <button type="button" class="btn btn-light btn-sm" (click)="cancelPlanForm()">Cancel</button>
    </div>

    <form class="plan-form-body" [formGroup]="planForm" (ngSubmit)="onSubmit()">
      <div class="form-grid">
        <div class="form-group">
          <label for="planId">Plan Name</label>
          <input type="text" id="planId" formControlName="planId" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planId.errors }">
          <div *ngIf="submitted && f.planId.errors" class="invalid-feedback">Plan name is required</div>
        </div>

        <div class="form-group">
          <label for="planType">Plan Type</label>
          <select id="planType" class="form-select" formControlName="planType">
            <option value="General">General</option>
            <option value="Hero Ensure">Hero Ensure</option>
          </select>
          <small class="text-muted d-block mt-1">Plan type classification. "Hero Ensure" plans are flagged for hero-specific reporting. Automatically set based on plan ID (starts with AA = Hero, else General). Can be manually overridden.</small>
        </div>

        <div class="form-group">
          <label for="planTotalAmount">Market Amount</label>
          <input type="number" id="planTotalAmount" formControlName="planTotalAmount" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planTotalAmount.errors }">
          <div *ngIf="submitted && f.planTotalAmount.errors" class="invalid-feedback">Market amount is required</div>
        </div>

        <div class="form-group">
          <label for="planNetAmount">Product Cost</label>
          <input type="number" id="planNetAmount" formControlName="planNetAmount" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planNetAmount.errors }">
          <div *ngIf="submitted && f.planNetAmount.errors" class="invalid-feedback">Product cost is required</div>
        </div>

        <div class="form-group">
          <label for="planAmount">Before GST</label>
          <input type="number" id="planAmount" formControlName="planAmount" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planAmount.errors }">
          <div *ngIf="submitted && f.planAmount.errors" class="invalid-feedback">Before GST amount is required</div>
        </div>

        <div class="form-group">
          <label for="planGstAmount">GST Amount</label>
          <input type="number" id="planGstAmount" formControlName="planGstAmount" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planGstAmount.errors }">
          <div *ngIf="submitted && f.planGstAmount.errors" class="invalid-feedback">GST amount is required</div>
        </div>

        <div class="form-group">
          <label for="planMaximumAmount">Maximum Amount</label>
          <input type="number" id="planMaximumAmount" formControlName="planMaximumAmount" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planMaximumAmount.errors }">
          <div *ngIf="submitted && f.planMaximumAmount.errors" class="invalid-feedback">Maximum amount is required</div>
        </div>

        <div class="form-group">
          <label for="planMinimumAmount">Minimum Amount</label>
          <input type="number" id="planMinimumAmount" formControlName="planMinimumAmount" class="form-control" [ngClass]="{ 'is-invalid': submitted && f.planMinimumAmount.errors }">
          <div *ngIf="submitted && f.planMinimumAmount.errors" class="invalid-feedback">Minimum amount is required</div>
        </div>

        <div class="form-group">
          <label for="planCPACoverAmount">CPA Cover Amount</label>
          <input type="number" id="planCPACoverAmount" formControlName="planCPACoverAmount" class="form-control">
        </div>
      </div>

      <!-- New row for Product Rack Rate, Operating Cost, Retention Cost, Net Benefit -->
      <div class="form-grid new-fields-row">
        <div class="form-group">
          <label for="productRackRate">Product Rack Rate</label>
          <input type="number" id="productRackRate" formControlName="productRackRate" class="form-control" [ngClass]="{ 'is-invalid': costExceedsProduct }" readonly>
          <div *ngIf="costExceedsProduct" class="invalid-feedback">Product Rack Rate is negative. Operating Cost + Retention Cost + Net Benefit exceeds Product Cost.</div>
        </div>

        <div class="form-group">
          <label for="operatingCost">Operating Cost</label>
          <input type="number" id="operatingCost" formControlName="operatingCost" class="form-control" [ngClass]="{ 'is-invalid': costExceedsProduct }">
          <div *ngIf="costExceedsProduct" class="invalid-feedback">Operating Cost + Retention Cost + Net Benefit cannot exceed Product Cost.</div>
        </div>

        <div class="form-group">
          <label for="retentionCost">Retention Cost</label>
          <input type="number" id="retentionCost" formControlName="retentionCost" class="form-control" [ngClass]="{ 'is-invalid': costExceedsProduct }">
          <div *ngIf="costExceedsProduct" class="invalid-feedback">Operating Cost + Retention Cost + Net Benefit cannot exceed Product Cost.</div>
        </div>

        <div class="form-group">
          <label for="netBenefit">Net Benefit</label>
          <input type="number" id="netBenefit" formControlName="netBenefit" class="form-control" [ngClass]="{ 'is-invalid': costExceedsProduct }">
          <div *ngIf="costExceedsProduct" class="invalid-feedback">Operating Cost + Retention Cost + Net Benefit cannot exceed Product Cost.</div>
        </div>
      </div>

      <div class="form-grid">
        <div class="form-group">
          <label for="rsaNumberOfServices">RSA Number of Services</label>
          <input type="number" id="rsaNumberOfServices" formControlName="rsaNumberOfServices" class="form-control">
        </div>

        <div class="form-group">
          <label for="numberOfYears">Number of Years</label>
          <input type="number" id="numberOfYears" formControlName="numberOfYears" class="form-control">
        </div>

        <div class="form-group">
          <label for="policyType">Policy Type</label>
          <select id="policyType" class="form-select" formControlName="policyType" [ngClass]="{ 'is-invalid': submitted && f.policyType.errors }">
            <option value="" disabled>Select policy type</option>
            <option *ngFor="let polType of printInstructions" [ngValue]="polType.policyType">{{ polType.policyType }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="masterPolicyNumber">Master Policy Number</label>
          <select id="masterPolicyNumber" class="form-select" formControlName="masterPolicyNumber">
            <option value="" disabled>Select master policy</option>
            <option *ngFor="let polMaint of policyMaintain" [ngValue]="polMaint.key">{{ polMaint.heading }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="counterId">Counter Series</label>
          <select id="counterId" class="form-select" formControlName="counterId">
            <option value="" disabled>Select counter series</option>
            <option *ngFor="let serMaint of seriesMaintain" [ngValue]="serMaint.key">{{ serMaint.heading }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="certificatePrefix">Certificate Prefix</label>
          <input type="text" id="certificatePrefix" formControlName="certificatePrefix" class="form-control">
        </div>

        <div class="form-group">
          <label for="printCpaPolicy">Print CPA Policy</label>
          <select id="printCpaPolicy" class="form-select" formControlName="printCpaPolicy">
            <option value="" disabled>Select option</option>
            <option *ngFor="let option of booleanArray" [ngValue]="option">{{ option }}</option>
          </select>
        </div>

        <div class="form-group">
          <label for="printRsaNumber">Print RSA Number</label>
          <select id="printRsaNumber" class="form-select" formControlName="printRsaNumber">
            <option value="" disabled>Select option</option>
            <option *ngFor="let option of booleanArray" [ngValue]="option">{{ option }}</option>
          </select>
        </div>

        <div class="form-group">
          <label>GST Show</label>
          <div class="radio-group">
            <label class="radio">
              <input type="radio" value="Y" formControlName="gstShow"> Yes
            </label>
            <label class="radio">
              <input type="radio" value="N" formControlName="gstShow"> No
            </label>
          </div>
        </div>

        <div class="form-group">
          <label>GST Applicable</label>
          <div class="radio-group">
            <label class="radio">
              <input type="radio" value="Y" formControlName="gstApplicable"> Yes
            </label>
            <label class="radio">
              <input type="radio" value="N" formControlName="gstApplicable"> No
            </label>
          </div>
        </div>

        <div class="form-group">
          <label for="applicableOnlyOnPolicyType">Policy Type Applicable On</label>
          <select id="applicableOnlyOnPolicyType" class="form-select" formControlName="applicableOnlyOnPolicyType">
            <option *ngFor="let option of applicableOnlyOnPolicyTypes" [ngValue]="option">{{ option }}</option>
          </select>
        </div>
      </div>

      <div class="form-actions">
        <button class="btn btn-primary" type="submit" [disabled]="loading">
          <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
          Save Plan
        </button>
        <button type="button" class="btn btn-secondary" (click)="cancelPlanForm()">Cancel</button>
      </div>
    </form>
  </div>

  <div class="plan-table-card">
    <div class="plan-table-header">
      <div>
        <h3>Published Plans</h3>
        <p class="text-muted mb-0">Monitor plan usage and manage dealer access quickly.</p>
      </div>
      <span class="badge bg-light text-dark">{{ filteredPlans.length }} / {{ plans.length }} plans</span>
    </div>

    <div class="plan-filter-toolbar">
      <div class="filter search">
        <label class="form-label">Search</label>
        <input
          type="search"
          class="form-control"
          placeholder="Search by plan, policy type or company"
          [(ngModel)]="planSearchTerm"
          [ngModelOptions]="{standalone: true}">
      </div>
      <div class="filter">
        <label class="form-label">Policy Type</label>
        <select
          class="form-select"
          [(ngModel)]="policyTypeFilter"
          [ngModelOptions]="{standalone: true}">
          <option value="">All</option>
          <option *ngFor="let option of policyTypeOptions" [ngValue]="option">{{ option }}</option>
        </select>
      </div>
      <div class="filter">
        <label class="form-label">Insurance Company</label>
        <select
          class="form-select"
          [(ngModel)]="insuranceCompanyFilter"
          [ngModelOptions]="{standalone: true}">
          <option value="">All</option>
          <option *ngFor="let option of insuranceCompanyOptions" [ngValue]="option">{{ option }}</option>
        </select>
      </div>
      <div class="filter actions">
        <label class="form-label">&nbsp;</label>
        <button
          type="button"
          class="btn btn-outline-secondary w-100"
          (click)="clearPlanFilters()"
          [disabled]="!planSearchTerm && !policyTypeFilter && !insuranceCompanyFilter">
          Clear
        </button>
      </div>
    </div>

    <div class="table-responsive">
      <div class="loading-overlay" *ngIf="loading">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <div class="loading-text">Loading plans…</div>
      </div>
      <table class="table plan-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Pricing</th>
            <th>Policy &amp; Settings</th>
            <th>Dealers &amp; Activity</th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let plan of filteredPlans">
            <td>
              <div class="plan-id">{{ plan.planId }}</div>
              <div class="plan-policy text-muted">{{ plan.policyType || '—' }}</div>
              <div class="plan-company text-muted">{{ plan.printInstruction?.policyCompany || 'Unassigned' }}</div>
            </td>
            <td>
              <div class="metric">
                <span class="label">Market</span>
                <span class="value">{{ plan.planTotalAmount | number:'1.2-2' }}</span>
              </div>
              <div class="metric">
                <span class="label">Before GST</span>
                <span class="value">{{ plan.planAmount | number:'1.2-2' }}</span>
              </div>
              <div class="metric">
                <span class="label">GST</span>
                <span class="value">{{ plan.planGstAmount | number:'1.2-2' }}</span>
              </div>
            </td>
            <td>
              <div class="chip-list">
                <span class="chip">CPA: {{ plan.printCpaPolicy || 'Yes' }}</span>
                <span class="chip">RSA: {{ plan.printRsaNumber || 'No' }}</span>
                <span class="chip">GST Show: {{ plan.gstShow === 'Y' ? 'Yes' : 'No' }}</span>
                <span class="chip">GST Applicable: {{ plan.gstApplicable === 'Y' ? 'Yes' : 'No' }}</span>
                <span class="chip">Applicable: {{ plan.applicableOnlyOnPolicyType || 'Both' }}</span>
              </div>
            </td>
            <td>
              <div class="dealer-summary">
                <div>
                  <strong>{{ plan.dealerCount }}</strong> dealer{{ plan.dealerCount === 1 ? '' : 's' }}
                  <button class="btn btn-link btn-sm ps-0" type="button" (click)="openDrilldown(plan)" [disabled]="plan.dealerCount === 0">View</button>
                </div>
                <div class="last-cert text-muted" *ngIf="plan.lastCertificate; else noCert">
                  Last issued on {{ plan.lastCertificate?.issueDate | date: 'mediumDate' }}
                  <div class="small text-muted">by {{ plan.lastCertificate?.dealerName }} ({{ plan.lastCertificate?.dealerId }})</div>
                </div>
                <ng-template #noCert>
                  <div class="text-muted small">No certificates issued yet</div>
                </ng-template>
              </div>
            </td>
            <td class="text-end">
              <div class="action-group">
                <button type="button" class="btn btn-outline-primary btn-sm" (click)="openAssignment(plan, 'add')">Assign to dealers</button>
                <button type="button" class="btn btn-outline-secondary btn-sm" (click)="openAssignment(plan, 'replace')">Replace in dealers</button>
                <button type="button" class="btn btn-info btn-sm" (click)="onEdit(plan)">Edit</button>
                <button type="button" class="btn btn-danger btn-sm" (click)="onDelete(plan)">Delete</button>
              </div>
            </td>
          </tr>
          <tr *ngIf="plans.length && !filteredPlans.length">
            <td colspan="5" class="text-center text-muted py-4">No plans match your filters.</td>
          </tr>
          <tr *ngIf="!plans.length">
            <td colspan="5" class="text-center text-muted py-4">No plans available yet.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</div>

<!-- Dealer drilldown modal -->
<div class="modal-backdrop" *ngIf="drilldown.visible">
  <div class="modal-card">
    <div class="modal-header">
      <h3>Dealers assigned to {{ drilldown.plan?.planId }}</h3>
      <button type="button" class="btn-close" (click)="closeDrilldown()"></button>
    </div>
    <div class="modal-body">
      <ng-container *ngIf="drilldown.plan?.dealerAssignments?.length; else noDealerState">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>Dealer ID</th>
              <th>Dealer Name</th>
              <th>Policy Type</th>
              <th class="text-end">Plan Amount</th>
              <th class="text-end">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let dealer of drilldown.plan?.dealerAssignments">
              <td>{{ dealer.dealerId }}</td>
              <td>{{ dealer.dealerName }}</td>
              <td>{{ dealer.policyType || '—' }}</td>
              <td class="text-end">{{ dealer.planAmount || '-' }}</td>
              <td class="text-end">{{ dealer.planTotalAmount || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </ng-container>
      <ng-template #noDealerState>
        <p class="text-muted mb-0">No dealers currently have this plan assigned.</p>
      </ng-template>
    </div>
    <div class="modal-footer">
      <button type="button" class="btn btn-secondary" (click)="closeDrilldown()">Close</button>
    </div>
  </div>
</div>

<!-- Bulk assignment modal -->
<div class="modal-backdrop" *ngIf="assignment.visible">
  <div class="modal-card large">
    <div class="modal-header">
      <div>
        <h3>{{ assignment.mode === 'add' ? 'Assign plan to dealers' : 'Replace plan for dealers' }}</h3>
        <p class="text-muted mb-0">
          Target: {{ assignment.plan?.planId }} · {{ assignment.plan?.printInstruction?.policyCompany || assignment.plan?.policyType }}
        </p>
      </div>
      <button type="button" class="btn-close" (click)="closeAssignment()"></button>
    </div>
    <div class="modal-body">
      <!-- Confirmation View -->
      <ng-container *ngIf="assignment.showConfirmation">
        <div class="confirmation-view">
          <div class="alert alert-warning">
            <strong>⚠️ Amount Differences Detected!</strong>
            <p class="mb-0 mt-2">The source and target plans have different amounts. Financial values will be preserved from the source plan during replacement. Please review the comparison below and confirm.</p>
          </div>

          <div class="comparison-summary mb-3">
            <strong>{{ assignment.selectedDealerIds.size }}</strong> dealer(s) will be updated.
          </div>

          <div class="plan-comparison-table">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Field</th>
                  <th class="source-col">Source: {{ assignment.sourcePlan?.planId }}</th>
                  <th class="target-col">Target: {{ assignment.plan?.planId }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let field of planComparisonFields" 
                    [ngClass]="{ 'highlight-diff': field.hasDifference, 'amount-diff': field.isAmount && field.hasDifference }">
                  <td class="field-label">{{ field.label }}</td>
                  <td class="source-col">{{ field.sourceValue }}</td>
                  <td class="target-col">{{ field.targetValue }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="comparison-legend mb-2 small text-muted">
            <span class="legend-item">⚡ = Field will be preserved from dealer's existing plan</span>
          </div>

          <div class="alert alert-info mt-3">
            <strong>What will happen:</strong>
            <ul class="mb-0 mt-2">
              <li>Fields marked with ⚡ (Amount, GST, Total, Net, Min, Max, GST Applicable, GST Show, Applicable On, Operating Cost) will be <strong>preserved</strong> from each dealer's current plan</li>
              <li>All other settings (Policy Type, CPA, RSA, Counter, etc.) will be <strong>updated</strong> to target plan values</li>
            </ul>
          </div>
        </div>
      </ng-container>

      <!-- Selection View -->
      <ng-container *ngIf="!assignment.showConfirmation">
        <div class="assignment-toolbar">
          <div class="mode-toggle">
            <label>
              <input type="radio" value="add" [checked]="assignment.mode === 'add'" (change)="setAssignmentMode('add')">
              Assign to additional dealers
            </label>
            <label>
              <input type="radio" value="replace" [checked]="assignment.mode === 'replace'" (change)="setAssignmentMode('replace')">
              Replace an existing plan
            </label>
          </div>

          <div *ngIf="assignment.mode === 'add'" class="alert alert-info mt-2 mb-0 py-2 px-3 small">
            <strong>Note:</strong> New plan assignment will use all values from the plan template including pricing.
          </div>

          <div *ngIf="assignment.mode === 'replace'" class="replace-select">
            <label>Plan to replace (Source)</label>
            <select class="form-select" [(ngModel)]="assignment.replacePlanId" (ngModelChange)="onReplacePlanChange($event)">
              <option value="" disabled>Select source plan to replace</option>
              <option *ngFor="let option of replacementPlanOptions" [ngValue]="option.planId">
                {{ option.planId }} · {{ option.policyType || option.printInstruction?.policyCompany }}
              </option>
            </select>
          </div>
        </div>

        <!-- Plan Comparison Table -->
        <div *ngIf="assignment.mode === 'replace' && assignment.sourcePlan" class="plan-comparison-section">
          <h5 class="comparison-title">Plan Comparison</h5>
          <div class="plan-comparison-table">
            <table class="table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Field</th>
                  <th class="source-col">Source: {{ assignment.sourcePlan?.planId }}</th>
                  <th class="target-col">Target: {{ assignment.plan?.planId }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let field of planComparisonFields" 
                    [ngClass]="{ 'highlight-diff': field.hasDifference, 'amount-diff': field.isAmount && field.hasDifference }">
                  <td class="field-label">{{ field.label }}</td>
                  <td class="source-col">{{ field.sourceValue }}</td>
                  <td class="target-col">{{ field.targetValue }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="comparison-legend mt-2 small text-muted">
            <span class="legend-item">⚡ = Field will be preserved from dealer's existing plan</span>
          </div>
          <div *ngIf="hasAmountDifferences" class="alert alert-warning mt-2 py-2 px-3 small">
            <strong>⚠️ Differences detected in preserved fields!</strong> These values will be kept from dealer's current plan during replacement.
          </div>
        </div>

        <!-- Dealer Selection -->
        <div *ngIf="assignment.mode === 'add' || (assignment.mode === 'replace' && assignment.replacePlanId)">
          <div class="dealer-search">
            <input class="form-control" type="search" placeholder="Search dealers by name or ID" [(ngModel)]="dealerSearch">
          </div>

          <div *ngIf="isLoadingDealers" class="text-center py-4">
            <span class="spinner-border spinner-border-sm me-2"></span>Loading dealers...
          </div>

          <div *ngIf="!isLoadingDealers" class="dealer-selection">
            <div *ngIf="assignment.mode === 'replace' && filteredAssignmentDealers.length === 0 && !dealerSearch" class="text-center text-muted py-4">
              No dealers have the source plan "{{ assignment.replacePlanId }}" assigned.
            </div>
            <table class="table table-hover table-sm" *ngIf="filteredAssignmentDealers.length > 0 || dealerSearch">
              <thead>
                <tr>
                  <th style="width: 48px;">
                    <input type="checkbox"
                      [checked]="selectableVisibleDealerCount > 0 && assignment.selectedDealerIds.size === selectableVisibleDealerCount"
                      [indeterminate]="assignment.selectedDealerIds.size > 0 && assignment.selectedDealerIds.size < selectableVisibleDealerCount"
                      (change)="toggleAllVisibleDealers($event.target.checked)">
                  </th>
                  <th>Dealer</th>
                  <th>Plan Type</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let option of filteredAssignmentDealers">
                  <td>
                    <input type="checkbox"
                      [checked]="isDealerSelected(option.dealerId)"
                      [disabled]="isDealerDisabled(option) || assignment.submitting"
                      (change)="toggleDealerSelection(option.dealerId, $event.target.checked)">
                  </td>
                  <td>
                    <div class="dealer-name">{{ option.dealerName }}</div>
                    <div class="dealer-id text-muted">{{ option.dealerId }}</div>
                  </td>
                  <td>{{ option.dealerPlanType || '—' }}</td>
                  <td>
                    <span class="status-pill" [ngClass]="{ 'assigned': option.hasPlan, 'eligible': assignment.mode === 'replace' && option.hasPlan }">
                      {{ getDealerStatusText(option) }}
                    </span>
                  </td>
                </tr>
                <tr *ngIf="filteredAssignmentDealers.length === 0 && dealerSearch">
                  <td colspan="4" class="text-center text-muted py-3">No dealers match your search.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </ng-container>
    </div>
    <div class="modal-footer">
      <div class="selection-summary text-muted">
        {{ assignment.selectedDealerIds.size }} dealer{{ assignment.selectedDealerIds.size === 1 ? '' : 's' }} selected
      </div>
      <div class="footer-actions">
        <ng-container *ngIf="assignment.showConfirmation">
          <button type="button" class="btn btn-secondary" (click)="cancelConfirmation()">Back</button>
          <button type="button" class="btn btn-warning" (click)="executeAssignment()" [disabled]="assignment.submitting">
            <span *ngIf="assignment.submitting" class="spinner-border spinner-border-sm me-2"></span>
            Confirm Replace
          </button>
        </ng-container>
        <ng-container *ngIf="!assignment.showConfirmation">
          <button type="button" class="btn btn-secondary" (click)="closeAssignment()">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="submitAssignment()" [disabled]="assignment.submitting">
            <span *ngIf="assignment.submitting" class="spinner-border spinner-border-sm me-2"></span>
            {{ assignment.mode === 'add' ? 'Assign plan' : 'Replace plan' }}
          </button>
        </ng-container>
      </div>
    </div>
  </div>
</div>
